import { z } from 'zod';
import { softEnum, softEnumWith } from './soft-enum.js';
import { isCountDenominator, isPeriodWord } from './amount-per.js';
import { AMOUNT_KINDS } from './grant-analysis.js';
import { eligibilityRowSchema } from './grant-eligibility.js';
import { normalizeIndustries } from './grant-codes.js';
import { normalizeBrief, BRIEF_FOCUSES, BRIEF_MAX_CHARS } from './grant-brief.js';
/**
 * 해석기가 보내는 지원 유형 (docs/HERMES.md §3, REFACTOR.md §2).
 *
 * 유형은 **돈이 어떤 형태로 오나**를 말한다. 화면의 기둥(자금·바우처 · 융자·보증 ·
 * 시설·입주·공간 · 프로그램)은 이 값을 `SUPPORT_TABLE` 로 접은 것이다 —
 * 보조금 · 바우처 · 상금은 셋 다 갚지 않는 돈이라 한 기둥(자금·바우처)이다.
 *
 * **`TAX_BENEFIT` 은 없다.** 키워드가 든 공고 259건 중 실제 세제 혜택이 6건
 * 이었다 — 나머지는 자격 심사 기준·제출서류 규정·용어 정의·결격 사유다.
 * 저장할 테이블이 없는 값을 열거형에 두면 해석기가 그걸 고르고 서버가 버린다.
 */
export const SUPPORT_TYPES = [
    'SUBSIDY',
    'VOUCHER',
    'PRIZE',
    'LOAN',
    'GUARANTEE',
    'FACILITY',
    'PROGRAM',
];
/**
 * 유형 → 기둥(저장 테이블).
 *
 * **1:1 이 아니다.** 테이블은 *유형*이 아니라 *모양*을 따른다 — `SUBSIDY` ·
 * `VOUCHER` · `PRIZE` 는 셋 다 「상한 + 비율」이고(`grant_fund`), `LOAN` 과
 * `GUARANTEE` 는 둘 다 「한도 + 요율 + 기간」이다(`grant_loan`). 어느 유형이 그
 * 행을 만들었는지는 `support_type` 컬럼이 말한다.
 */
export const SUPPORT_TABLE = {
    SUBSIDY: 'grant',
    VOUCHER: 'grant',
    PRIZE: 'grant',
    LOAN: 'loan',
    GUARANTEE: 'loan',
    FACILITY: 'facility',
    PROGRAM: 'program',
};
/** 자금·바우처 행의 갈래. `support_type` 에서 읽는다 — 화면의 기준은 아니다 (REFACTOR §3.1). */
export const FUND_KINDS = ['subsidy', 'voucher', 'prize'];
export const FUND_KIND_OF = {
    SUBSIDY: 'subsidy',
    VOUCHER: 'voucher',
    PRIZE: 'prize',
};
/**
 * 프로그램의 하위 종류 (REFACTOR.md §2). **식별자가 아니다** — 그건 `name` 이다 (`0021`).
 * 화면의 필터 알약(`?kind=`)에 쓴다.
 *
 * 교육 · 컨설팅 · 멘토링은 한 묶음(`learning`)이다 — 적극적으로 찾는 사람이 없고,
 * 셋을 가르면 필터가 셋 다 얇아진다. 공간 · 장비는 프로그램이 아니라 기둥(`facility`)이다.
 */
export const PROGRAM_KINDS = [
    'learning',
    'networking',
    'contest',
    'fair',
    'sales',
    'overseas',
    'other',
];
/** 시설·입주·공간의 갈래. */
export const FACILITY_KINDS = ['space', 'equipment', 'incubation'];
/**
 * 자부담의 **기준(분모)**.
 *
 * 「자부담 20%」만으로는 무엇의 20%인지 모른다. 원문 548건(30%)이 자부담을
 * 말하는데 두 뜻이 섞여 있다:
 *
 *   on_top    「기업당 최대 430만원 … 기업부담금: 지원금액의 10%」
 *             → 430만원을 받고 43만원을 **따로** 낸다. 지원은 430만원 그대로.
 *   included  「총 125만원 중 정부보조 100만원(80%), 기업부담 25만원(20%)」
 *             → 총사업비 기준. 자부담 25만원, 지원 100만원.
 *
 * 이 값이 있어야 자부담 **금액**을 계산할 수 있다. 없으면 계산하지 않는다 —
 * 모르는 것을 아는 척하지 않는다.
 */
export const SELF_PAY_BASES = ['on_top', 'included'];
/**
 * 근거 하나.
 *
 * **필드마다 따로 있어야 한다.** 융자 행에는 숫자가 셋인데(한도·금리·기간)
 * 근거 문장이 하나면 나머지 둘은 대조할 방법이 없다 — 검증되지 않은 숫자가
 * 검증된 숫자 옆에 앉아 같은 신뢰도로 보인다. 그게 가장 나쁜 형태다.
 */
export const evidenceItemSchema = z.object({
    /** 원문에서 잘라낸 그대로. 다듬으면 대조에 실패해 거부된다 — 그게 의도다. */
    quote: z.string().min(1).max(500),
    confidence: z.number().int().min(0).max(100),
});
/**
 * 해석기가 보내는 행 하나.
 *
 * 숫자 필드는 **`evidence` 에 같은 이름의 근거가 있어야** 저장된다. 없으면
 * 그 필드만 버리고 나머지는 남긴다 — 한도는 검증됐는데 금리만 어긋났다면
 * 한도까지 버릴 이유가 없다.
 */
export const supportRowSchema = z.object({
    /*
      **`GRANT` 는 `SUBSIDY` 로 접는다.** 둘은 같은 뜻(갚지 않는 돈)인데 이름이 둘이라
      모델이 번갈아 골랐고(실측 GRANT 83행 / SUBSIDY 2,423행), 공개 화면은 둘 다
      「보조금」으로 보이지만 콘솔은 저장된 값을 그대로 찍어 한 공고만 다르게 보였다
      (ed2a6fbf 상금 세 줄). 옛 해석기 출력이 아직 GRANT 를 보내도 여기서 하나로 만든다.
    */
    supportType: z.preprocess((v) => (v === 'GRANT' ? 'SUBSIDY' : v), z.enum(SUPPORT_TYPES)),
    /**
     * 지원 항목의 이름. **이게 행의 식별자다.**
     *
     * 공고문은 지원을 *이름 붙은 항목*으로 쓴다 — 표의 「지원분야」 칸이
     * 하드웨어 / 기구 / 사용자실증으로 나뉘고 각각이 별개 지원이다. 이름이
     * 없으면 셋을 구분할 수단이 없어 하나로 접히고, 화면에도 붙일 라벨이 없다.
     *
     * **원문에 그대로 있어야 저장된다.** 숫자에 건 규칙을 이름에도 건다 —
     * 지어낸 이름은 지어낸 숫자만큼 사람을 오도한다. 대조에 실패하면 이름을
     * 비우고, 이름 없는 행끼리는 하나로 접힌다(안전한 쪽으로 무너진다).
     */
    name: z.string().min(1).max(120).nullable().default(null),
    /**
     * 조건별 차등 지급의 **조건** (`0030`). 같은 이름의 행이 조건에 따라 금액이
     * 다를 때 행을 나누고 여기에 조건을 적는다 — 「수출 10만불 미만」「1박」.
     * 원문에 있어야 저장된다. 조건이 없으면 `null`.
     */
    condition: z.string().min(1).max(80).nullable().default(null),
    /** `maximumAmount` 가 무엇인가. `per_company_cap` 이 아니면 금액으로 안 쓴다. */
    amountKind: softEnumWith(AMOUNT_KINDS, 'none'),
    /* --- 금전 --- */
    /** 원 단위 정수. 기업당 상한. */
    maximumAmount: z.number().int().positive().nullable().default(null),
    /**
     * `maximumAmount` 의 **분모** (`0028`). 「1인당 30만원」이면 `"인"`.
     *
     * 낱말 하나만 보낸다 — `"1인당"` 도 `"인당"` 도 아니고 `"인"` 이다.
     * 신청자 하나당이면 비운다. 자세한 것은 `amount-per.ts`.
     *
     * **주석이라 느슨하게 받는다.** 모양이 어긋나도 이 칸만 비우고 금액은
     * 살린다 — 「금액 30만원」이 「분모를 못 읽었다」로 통째로 죽으면 안 된다.
     */
    amountPer: z
        .unknown()
        .transform((v) => (typeof v === 'string' && isCountDenominator(v) ? v.trim() : null)),
    /**
     * `maximumAmount` 의 **기간 축** (`0029`). 「1인당 월 30만원」이면 `"월"`.
     * `amountPer` 와 같이 올 수 있다 — 다른 축이다. 총액이 원문에 있으면 총액을
     * 보내고 이 칸은 비운다. 자세한 것은 `amount-per.ts`.
     */
    amountPeriod: z
        .unknown()
        .transform((v) => (typeof v === 'string' && isPeriodWord(v) ? v.trim() : null)),
    /** 총사업비 대비 지원 비율 (1~100). */
    ratio: z.number().int().min(1).max(100).nullable().default(null),
    /**
     * **이 돈이 지분의 대가인가.**
     *
     * 투자는 갚지 않는다는 점에서 보조금과 같지만 **회사 일부를 가져간다.**
     * 화면이 「보조금 3억」이라고 말하면 공짜 돈으로 읽히는데, 실제로는 지분을
     * 파는 것이다. 「더인벤션랩 직접투자(1~3억)」이 그 예다.
     *
     * 테이블을 따로 만들지 않고 플래그로 둔 이유: **아직 얼마나 흔한지 모른다.**
     * `program_kind` 를 식별자로 썼다가 되돌린 적이 있고(`0020`→`0021`), 세제는
     * 실측 2.3% 를 보고 아예 안 만들었다. 분포를 모르는 채 테이블을 만들면
     * 또 되돌린다. 이 플래그가 그 분포를 재는 자다.
     */
    isEquity: z.boolean().default(false),
    /** 신청자가 내는 몫 (1~100). **무엇의 몫인지는 `selfPayBasis` 가 말한다.** */
    /** 0 은 「자부담 없음」이라는 뜻이 있는 값이다 — 매출 구간별 분담금 표의 첫 칸(실측 fabb28d3). */
    selfPayRatio: z.number().int().min(0).max(100).nullable().default(null),
    /** `selfPayRatio` 의 분모. 없으면 자부담 금액을 계산하지 않는다. */
    selfPayBasis: softEnum(SELF_PAY_BASES),
    /** 자부담 **금액** (원). 원문이 비율이 아니라 금액으로 말할 때. */
    selfPayAmount: z.number().int().positive().nullable().default(null),
    /* --- 융자·보증 --- */
    /** 연이율. **0.01%p 단위 정수** (2.5% → 250). */
    interestRate: z.number().int().min(0).max(10_000).nullable().default(null),
    /**
     * 이차보전·금리감면 **폭**. 0.01%p 단위 (2.3% 감면 → 230).
     *
     * **`interestRate` 와 다른 축이다.** 「2.3% 감면」을 금리 칸에 넣으면 화면이
     * 「금리 2.3%」라고 말하는데, 실제 금리는 기준금리 빼기 2.3%p 다 — 정반대에
     * 가까운 거짓말이다. 그래서 모델이 보내기를 거부했고, 그 결과 정보가 통째로
     * 사라졌다. 실측 이차보전 125건 + 금리우대 32건으로 금리 명시(151건)만큼
     * 흔하다.
     */
    interestSubsidy: z.number().int().min(0).max(10_000).nullable().default(null),
    /**
     * 보증료율. 0.01%p 단위 (연 0.8% → 80).
     *
     * **보증의 핵심 숫자다.** 보증에는 금리가 없다 — 돈을 빌려주는 게 아니라
     * 남의 대출을 서 주는 것이라 받는 것은 보증료다. 이 필드가 없어서 보증 행은
     * 한도와 기간만 남고 가격이 비어 있었다. `guaranteeRatio`(보증비율, 대출액
     * 중 서 주는 몫)와는 다른 축이다.
     */
    guaranteeFeeRate: z.number().int().min(0).max(10_000).nullable().default(null),
    loanTermMonths: z.number().int().min(1).max(600).nullable().default(null),
    graceMonths: z.number().int().min(0).max(600).nullable().default(null),
    /** 대출액 중 보증기관이 서는 몫 (1~100). `ratio` 와 다른 축이다. */
    guaranteeRatio: z.number().int().min(1).max(100).nullable().default(null),
    /* --- 바우처 --- */
    usageScope: z.string().max(300).nullable().default(null),
    /**
     * **부대비용** (`0052`). 같은 공고의 프로그램 · 시설 행에 참가해야만 받는 비용 보조 —
     * 참가비 · 부스비 · 항공 · 숙박 · 통역 · 실증비. 돈은 돈으로 저장하되 **기둥은 그 돈이 딸린
     * 프로그램이 정한다**: 대표 기둥을 못 정하고, 자금·바우처 탭 소속에 안 센다. 실측 해외 전시
     * 파견 공고의 「왕복항공료 50%」가 첫 칸에 「자금·바우처」로 섰다 — 사용자가 그 탭에서 찾는
     * 「내 통장에 오는 돈」이 아니다. 분류값이라 대조하지 않는다.
     */
    ancillary: z.boolean().default(false),
    /* --- 시설·입주·공간 --- */
    facilityKind: softEnum(FACILITY_KINDS),
    /** 임대료 감면 비율 (1~100). 「임대료 50% 감면」. 원문 인용 검증을 거친다. */
    rentDiscountRatio: z.number().int().min(1).max(100).nullable().default(null),
    /* --- 프로그램 --- */
    programKind: softEnum(PROGRAM_KINDS),
    durationMonths: z.number().int().min(1).max(600).nullable().default(null),
    /**
     * 몇을 뽑는가. 「총 12개 제품」「20개사」「총 8건」.
     *
     * **프로그램 전용이 아니다.** 「AI 내재화 기술지도 총 8건」은 바우처 행인데
     * 프로그램에만 이 칸이 있어서 8이 버려졌다. 신청자가 경쟁률을 가늠하는
     * 유일한 숫자라 네 테이블 모두에 둔다.
     */
    capacity: z.number().int().min(1).max(100_000).nullable().default(null),
    /** 키가 위 숫자 필드명이다. 서버가 키를 보고 어느 값과 대조할지 안다. */
    evidence: z.record(z.string(), evidenceItemSchema).default({}),
});
/**
 * 한 공고의 해석 결과.
 *
 * **한 공고에 여러 유형이 있다.** 중소기업육성자금 하나에 융자와 이차보전이
 * 같이 있고, 창업패키지 하나에 사업화자금과 멘토링이 같이 있다. 그래서 배열이다.
 */
export const supportAnalysisSchema = z.object({
    /**
     * **20까지 받는다.** 12이던 때 실측에서 막혔다 — 부산 수출 스타기업 공고
     * 하나가 지원항목을 정확히 12개 채워 왔다. 상한에 딱 맞는 값은 「12개였다」가
     * 아니라 **「거기서 잘렸다」**로 읽어야 한다.
     *
     * 그 전에는 6이었고 온디바이스AI 공고(유형1 셋 + 유형2 셋 + 상시지원)에서
     * 같은 식으로 막혔다. 두 번 같은 이유로 올렸다.
     */
    rows: z.array(supportRowSchema).max(20),
    /**
     * 신청 자격. **지원 행과 같은 요청에 실린다** — 한 문서를 한 번 읽고 둘 다
     * 뽑는 게 자연스럽고, 두 번 부르면 같은 문서를 두 번 읽어야 한다.
     *
     * 40까지 받는다. 20이던 때 파일럿 25건 중 다섯 건이 정확히 20을 채웠다 —
     * 결격사유는 한 공고가 열 줄 넘게 나열하는 게 보통이다.
     */
    eligibility: z.array(eligibilityRowSchema).max(40).default([]),
    /**
     * 신청 기업의 업종 (REFACTOR §3.2). 공고 단위다 — 목록 필터와 매칭이 읽는다.
     * 모르는 코드는 떨어지고 항목은 남는다. `all` 이면 전 업종이다.
     */
    industries: z.unknown().transform(normalizeIndustries),
    /** 어느 모델이 판정했는지. 모델을 바꿨을 때 비교하려면 남아야 한다. */
    model: z.string().min(1).max(120),
    /** 상세에 실을 우리 문장. `briefFocus` 와 함께 오거나 함께 없다 (`normalizeBrief`). */
    brief: z.string().max(BRIEF_MAX_CHARS).optional(),
    briefFocus: z.enum(BRIEF_FOCUSES).optional(),
});
/**
 * **항목 하나가 상해도 문서를 통째로 버리지 않는다.**
 *
 * `supportAnalysisSchema` 를 그대로 쓰면 zod 는 전부 아니면 전무다. 실측에서
 * 그 대가를 봤다 — 파일럿 25건 중 두 건이 422 로 죽었는데, 사유는 각각
 * 필드 하나였다:
 *
 *   rows.0.selfPayRatio: Expected integer, received float
 *   eligibility.10.unit: Invalid enum value ... received 'months'
 *
 * 첫 번째 문서는 지원 행 여럿과 자격 조건 스무 개를 멀쩡히 담고 있었다.
 * 소수점 하나 때문에 **전부** 버려졌고, 해석에 쓴 LLM 호출도 같이 버려졌다.
 * 큐는 그 문서를 다시 뱉으므로 다음 실행에서 같은 값을 내고 또 죽는다.
 *
 * 이건 이 시스템의 원칙과도 어긋난다 — 검증은 **필드 단위**이고 「거부는
 * 오류가 아니라 결과」다 (`docs/HERMES.md` §2). 문 앞에서만 전부 아니면
 * 전무였던 것이다.
 *
 * 그래서 항목마다 따로 검사하고 통과한 것만 넘긴다. **느슨해지는 게 아니다** —
 * 각 항목은 여전히 스키마 전체를 통과해야 하고, 실패한 항목은 값을 고쳐
 * 넣는 게 아니라 **버린다.** 몇 개를 버렸는지는 응답에 실어 보낸다. 조용히
 * 사라지면 계약이 깨진 것을 아무도 모른다.
 *
 * 봉투(`model`, 그리고 rows/eligibility 가 배열이라는 것) 자체가 깨졌으면
 * 그때는 422 다. 그건 항목의 문제가 아니라 해석기가 계약을 안 지킨 것이다.
 */
export const analysisEnvelopeSchema = z.object({
    rows: z.array(z.unknown()).max(20),
    eligibility: z.array(z.unknown()).max(40).default([]),
    industries: z.unknown().optional(),
    model: z.string().min(1).max(120),
    /*
      **모양을 여기서 따지지 않는다.** 판단은 `normalizeBrief` 한 곳에 있다.
  
      처음에는 5,000자 하드 캡과 40자 focus 를 두었는데, 그러면 브리프 하나가
      길거나 타입이 틀렸다는 이유로 봉투가 422 가 되고 rows · eligibility 가 같이
      죽는다. 그리고 그 422 는 러너의 차단기를 먹인다 — 10건 연속이면 backfill
      전체가 멈춘다. 브리프 모양 실수 하나가 2,748건 재해석을 태울 수 있었다.
  
      `salvageAnalysis` 를 만든 이유가 정확히 이것이다: **항목 하나가 문서를
      통째로 버리게 두지 않는다.**
    */
    brief: z.unknown(),
    briefFocus: z.unknown(),
});
/** 사유를 몇 개까지 남길지. 한 문서가 전부 깨졌을 때 로그를 채우지 않도록. */
const MAX_ISSUES = 10;
export function salvageAnalysis(env) {
    const issues = [];
    // 스키마 자체를 제네릭으로 잡는다. `z.ZodType<T>` 로 받으면 T 가 입력 타입에
    // 묶여, `.default()` 가 붙은 필드가 optional 인 채로 새어 나온다.
    const keep = (where, schema, items) => {
        const kept = [];
        let dropped = 0;
        for (const [i, item] of items.entries()) {
            const r = schema.safeParse(item);
            if (r.success) {
                kept.push(r.data);
                continue;
            }
            dropped += 1;
            /*
              **어느 항목의 어느 필드가 왜 틀렸는지**를 그대로 적는다. zod 가 이미
              그 말을 갖고 있는데 개수만 세고 버리면, 나중에 「스키마 미달 1」만
              보고 아무것도 못 고친다.
            */
            for (const issue of r.error.issues) {
                if (issues.length >= MAX_ISSUES)
                    break;
                issues.push(`${where}[${i}].${issue.path.join('.') || '(전체)'}: ${issue.message}`);
            }
        }
        return { kept, dropped };
    };
    const rows = keep('rows', supportRowSchema, env.rows);
    const elig = keep('eligibility', eligibilityRowSchema, env.eligibility);
    // 브리프는 배열이 아니라 한 덩어리다. 통과하면 싣고, 아니면 사유만 남긴다.
    const brief = normalizeBrief(env.brief, env.briefFocus);
    if (!brief && (env.brief || env.briefFocus) && issues.length < MAX_ISSUES) {
        issues.push(`brief: 상한(${BRIEF_MAX_CHARS}자) 초과이거나 focus 가 없거나 열거 밖이다`);
    }
    return {
        input: {
            rows: rows.kept,
            eligibility: elig.kept,
            industries: normalizeIndustries(env.industries),
            model: env.model,
            ...(brief ? { brief: brief.brief, briefFocus: brief.focus } : {}),
        },
        malformed: { rows: rows.dropped, eligibility: elig.dropped, issues },
    };
}
//# sourceMappingURL=grant-support.js.map